/**
 * Test and demo accounts (PBI #1347) — super admin only.
 *
 * Rendered inside the admin customer detail modal. Lets a super admin comp Personal with the admin
 * grant (no Stripe involved), revoke it, and flag or unflag the account as a demo account. The
 * server enforces SuperAdmin on every endpoint; this component only hides the controls from
 * support agents and content managers.
 *
 * Flagging is guarded twice: a warning dialog with an "I understand" tick here, and an explicit
 * `acknowledged` field the API requires, so a direct API call cannot skip the warning. The API also
 * refuses to flag an account that already has orders, payments, or a Stripe subscription.
 */

import { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Badge,
  Button,
  Checkbox,
  Divider,
  Group,
  List,
  NumberInput,
  Select,
  Stack,
  Text,
  TextInput,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { notifications } from '@mantine/notifications';
import { IconAlertTriangle } from '@tabler/icons-react';
import { AppModal } from '../common';
import { useAuth } from '../../contexts/AuthContext';
import { useModalZIndex } from '../../hooks/useModalZIndex';
import { useScrollToFirstError } from '../../hooks/useScrollToFirstError';
import { getSubscriptionPlans } from '../../services/subscriptionService';
import {
  MAX_GRANT_DURATION_DAYS,
  getAdminTestAccountStatus,
  grantAdminSubscription,
  revokeAdminSubscription,
  setAdminDemoAccount,
  type AdminTestAccountStatus,
} from '../../services/adminTestAccountsService';
import { brandColors, semanticColors } from '../../config/theme';
import { formatInstantLocalDate } from '../../utils/formatDateTime';
import type { SubscriptionPlan } from '../../types/subscription';
import { DemoAccountPill } from './DemoAccountPill';

interface CustomerTestAccountSectionProps {
  /** Customer user id. */
  customerId: string;
  /** Whether the parent customer modal is open (re-fetches state on open). */
  opened: boolean;
  /** Called after the demo flag, a grant, or a revoke changes so lists can refresh. */
  onChanged?: (status: AdminTestAccountStatus) => void;
}

interface GrantFormValues {
  planId: string;
  billingInterval: 'Monthly' | 'Annual';
  durationDays: number;
}

interface DemoFormValues {
  acknowledged: boolean;
  reason: string;
}

const BILLING_INTERVAL_OPTIONS = [
  { value: 'Annual', label: 'Annual' },
  { value: 'Monthly', label: 'Monthly' },
];

const errorMessage = (err: unknown, fallback: string): string => {
  const axiosError = err as { response?: { data?: { message?: string } } };
  return axiosError.response?.data?.message ?? fallback;
};

export const CustomerTestAccountSection = (props: CustomerTestAccountSectionProps) => {
  const { isSuperAdmin } = useAuth();
  if (!isSuperAdmin) {
    return null;
  }
  return <SuperAdminTestAccountSection {...props} />;
};

const SuperAdminTestAccountSection = ({
  customerId,
  opened,
  onChanged,
}: CustomerTestAccountSectionProps) => {
  const zIndex = useModalZIndex(opened);
  const [status, setStatus] = useState<AdminTestAccountStatus | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [plans, setPlans] = useState<SubscriptionPlan[]>([]);
  const [grantOpened, setGrantOpened] = useState(false);
  const [revokeOpened, setRevokeOpened] = useState(false);
  const [demoOpened, setDemoOpened] = useState(false);
  const [saving, setSaving] = useState(false);

  const loadStatus = useCallback(async () => {
    try {
      setLoadError(null);
      setStatus(await getAdminTestAccountStatus(customerId));
    } catch {
      setLoadError('Failed to load test and demo account state.');
    }
  }, [customerId]);

  useEffect(() => {
    if (!opened) {
      setStatus(null);
      setGrantOpened(false);
      setRevokeOpened(false);
      setDemoOpened(false);
      return;
    }
    void loadStatus();
  }, [opened, loadStatus]);

  const applyStatus = (next: AdminTestAccountStatus) => {
    setStatus(next);
    onChanged?.(next);
  };

  const grantForm = useForm<GrantFormValues>({
    initialValues: { planId: '', billingInterval: 'Annual', durationDays: MAX_GRANT_DURATION_DAYS },
    validate: {
      planId: (value) => (value ? null : 'Choose a plan'),
      durationDays: (value) =>
        Number.isInteger(value) && value >= 1 && value <= MAX_GRANT_DURATION_DAYS
          ? null
          : `Enter a whole number of days from 1 to ${MAX_GRANT_DURATION_DAYS}`,
    },
    validateInputOnBlur: true,
  });
  const scrollToFirstGrantError = useScrollToFirstError(grantForm);

  const demoForm = useForm<DemoFormValues>({
    initialValues: { acknowledged: false, reason: '' },
    validate: {
      acknowledged: (value) => (value ? null : 'Tick to confirm you understand'),
      reason: (value) => (value.length > 500 ? 'Keep the note under 500 characters' : null),
    },
    validateInputOnBlur: true,
  });
  const scrollToFirstDemoError = useScrollToFirstError(demoForm);

  const openGrant = async () => {
    grantForm.reset();
    setGrantOpened(true);
    if (plans.length > 0) {
      return;
    }
    try {
      const loaded = await getSubscriptionPlans();
      setPlans(loaded);
      const personal = loaded.find((p) => p.tier.toLowerCase() === 'personal') ?? loaded[0];
      if (personal) {
        grantForm.setFieldValue('planId', String(personal.id));
      }
    } catch {
      notifications.show({
        title: 'Error',
        message: 'Failed to load subscription plans.',
        color: brandColors.danger,
      });
    }
  };

  const openDemo = () => {
    demoForm.reset();
    setDemoOpened(true);
  };

  const handleGrant = grantForm.onSubmit(async (values) => {
    setSaving(true);
    try {
      await grantAdminSubscription(customerId, {
        planId: Number(values.planId),
        billingInterval: values.billingInterval,
        durationDays: values.durationDays,
      });
      setGrantOpened(false);
      notifications.show({
        title: 'Subscription granted',
        message: `Granted for ${values.durationDays} days. No Stripe subscription was created.`,
        color: brandColors.success,
      });
      const next = await getAdminTestAccountStatus(customerId);
      applyStatus(next);
    } catch (err: unknown) {
      notifications.show({
        title: 'Error',
        message: errorMessage(err, 'Failed to grant the subscription.'),
        color: brandColors.danger,
      });
    } finally {
      setSaving(false);
    }
  }, scrollToFirstGrantError);

  const handleRevoke = async () => {
    setSaving(true);
    try {
      await revokeAdminSubscription(customerId);
      setRevokeOpened(false);
      notifications.show({
        title: 'Subscription revoked',
        message: 'The account is back on the free plan.',
        color: brandColors.warning,
      });
      applyStatus(await getAdminTestAccountStatus(customerId));
    } catch (err: unknown) {
      notifications.show({
        title: 'Error',
        message: errorMessage(err, 'Failed to revoke the subscription.'),
        color: brandColors.danger,
      });
    } finally {
      setSaving(false);
    }
  };

  const setDemo = async (isDemoAccount: boolean, acknowledged: boolean, reason?: string) => {
    setSaving(true);
    try {
      const next = await setAdminDemoAccount(customerId, {
        isDemoAccount,
        acknowledged,
        reason: reason?.trim() ? reason.trim() : undefined,
      });
      setDemoOpened(false);
      applyStatus(next);
      notifications.show({
        title: isDemoAccount ? 'Demo account enabled' : 'Demo account cleared',
        message: isDemoAccount
          ? 'Orders on this account are now simulated and outbound email is suppressed.'
          : 'New orders on this account are real again. Past simulated orders stay simulated.',
        color: isDemoAccount ? brandColors.demo : brandColors.success,
      });
    } catch (err: unknown) {
      notifications.show({
        title: 'Error',
        message: errorMessage(err, 'Failed to update the demo account flag.'),
        color: brandColors.danger,
      });
    } finally {
      setSaving(false);
    }
  };

  const handleMarkDemo = demoForm.onSubmit(
    (values) => setDemo(true, values.acknowledged, values.reason),
    scrollToFirstDemoError,
  );

  const subscription = status?.activeSubscription ?? null;

  return (
    <>
      <Divider label="Test and demo accounts" labelPosition="center" />

      {loadError && (
        <Alert color={brandColors.danger} icon={<IconAlertTriangle size={16} />}>
          {loadError}
        </Alert>
      )}

      {status && (
        <Stack gap="sm" data-testid="test-account-section">
          <Group justify="space-between" wrap="nowrap">
            <Text fw={500} size="sm">
              Granted plan
            </Text>
            {subscription ? (
              <Stack gap={0} align="flex-end">
                <Text size="sm">
                  {subscription.planName} ({subscription.billingInterval})
                  {subscription.isAdminGrant ? ' - admin grant' : ''}
                </Text>
                <Text size="xs" c={semanticColors.text.secondary}>
                  Expires {formatInstantLocalDate(subscription.currentPeriodEnd)}
                </Text>
              </Stack>
            ) : (
              <Text size="sm">None</Text>
            )}
          </Group>

          <Group justify="space-between" wrap="nowrap">
            <Text fw={500} size="sm">
              Demo account
            </Text>
            {status.isDemoAccount ? (
              <DemoAccountPill size="sm" />
            ) : (
              <Badge color={brandColors.neutral} size="sm" variant="light">
                No
              </Badge>
            )}
          </Group>

          <Text size="sm" c={semanticColors.text.secondary}>
            A comped test account without the demo flag still transacts for real. The demo flag
            simulates orders and suppresses email, and can only be set on an empty account.
          </Text>

          {!status.isDemoAccount && !status.canMarkDemo && (
            <Alert color={brandColors.warning} icon={<IconAlertTriangle size={16} />}>
              Cannot be marked as a demo account. Blocked by: {status.demoBlockers.join(', ')}.
            </Alert>
          )}

          <Group gap="sm">
            <Button variant="light" onClick={() => void openGrant()} disabled={saving}>
              Grant Personal
            </Button>
            {subscription && (
              <Button
                variant="light"
                color={brandColors.warning}
                onClick={() => setRevokeOpened(true)}
                disabled={saving}
              >
                Revoke plan
              </Button>
            )}
            {status.isDemoAccount ? (
              <Button
                variant="light"
                color={brandColors.demo}
                onClick={() => setDemoOpened(true)}
                disabled={saving}
              >
                Unmark demo account
              </Button>
            ) : (
              <Button
                variant="light"
                color={brandColors.demo}
                onClick={openDemo}
                disabled={saving || !status.canMarkDemo}
              >
                Mark as demo account
              </Button>
            )}
          </Group>
        </Stack>
      )}

      <AppModal
        id="customer-grant-subscription"
        opened={grantOpened}
        onClose={() => !saving && setGrantOpened(false)}
        zIndex={zIndex + 1}
        title="Grant Personal"
        size="sm"
        bodyHeight="auto"
        footer={
          <Group justify="flex-end">
            <Button variant="default" onClick={() => setGrantOpened(false)} disabled={saving}>
              Cancel
            </Button>
            <Button onClick={() => void handleGrant()} loading={saving}>
              Grant
            </Button>
          </Group>
        }
      >
        <Stack gap="sm">
          <Text size="sm" c={semanticColors.text.secondary}>
            Comps the plan without Stripe. The account is not charged and no Stripe subscription is
            created.
          </Text>
          <Select
            label="Plan"
            data={plans.map((p) => ({ value: String(p.id), label: p.name }))}
            allowDeselect={false}
            {...grantForm.getInputProps('planId')}
          />
          <Select
            label="Billing interval"
            data={BILLING_INTERVAL_OPTIONS}
            allowDeselect={false}
            {...grantForm.getInputProps('billingInterval')}
          />
          <NumberInput
            label="Duration (days)"
            min={1}
            max={MAX_GRANT_DURATION_DAYS}
            allowDecimal={false}
            {...grantForm.getInputProps('durationDays')}
          />
        </Stack>
      </AppModal>

      <AppModal
        id="customer-revoke-subscription"
        opened={revokeOpened}
        onClose={() => !saving && setRevokeOpened(false)}
        zIndex={zIndex + 1}
        title="Revoke plan?"
        size="sm"
        bodyHeight="auto"
        footer={
          <Group justify="flex-end">
            <Button variant="default" onClick={() => setRevokeOpened(false)} disabled={saving}>
              Cancel
            </Button>
            <Button color={brandColors.warning} onClick={() => void handleRevoke()} loading={saving}>
              Revoke plan
            </Button>
          </Group>
        }
      >
        <Text size="sm">
          The account returns to the free plan immediately. Contacts above the free limit lock until
          the account subscribes again.
        </Text>
      </AppModal>

      <AppModal
        id="customer-demo-account"
        opened={demoOpened}
        onClose={() => !saving && setDemoOpened(false)}
        zIndex={zIndex + 1}
        title={status?.isDemoAccount ? 'Unmark demo account?' : 'Mark as demo account?'}
        size="sm"
        bodyHeight="auto"
        footer={
          status?.isDemoAccount ? (
            <Group justify="flex-end">
              <Button variant="default" onClick={() => setDemoOpened(false)} disabled={saving}>
                Cancel
              </Button>
              <Button
                color={brandColors.demo}
                onClick={() => void setDemo(false, false)}
                loading={saving}
              >
                Unmark demo account
              </Button>
            </Group>
          ) : (
            <Group justify="flex-end">
              <Button variant="default" onClick={() => setDemoOpened(false)} disabled={saving}>
                Cancel
              </Button>
              <Button
                color={brandColors.danger}
                onClick={() => void handleMarkDemo()}
                loading={saving}
                disabled={!demoForm.values.acknowledged}
              >
                Mark as demo account
              </Button>
            </Group>
          )
        }
      >
        {status?.isDemoAccount ? (
          <Text size="sm">
            New orders on this account become real: they are charged and fulfilled, and email is
            sent again. Orders placed while it was a demo account stay simulated.
          </Text>
        ) : (
          <Stack gap="sm">
            <Alert color={brandColors.danger} icon={<IconAlertTriangle size={16} />}>
              This bypasses payment and fulfillment for the account.
            </Alert>
            <List size="sm" spacing={4}>
              <List.Item>Orders are never charged and never shipped.</List.Item>
              <List.Item>Outbound email is suppressed.</List.Item>
              <List.Item>
                Its records are excluded from aggregate reports and ad conversion uploads.
              </List.Item>
              <List.Item>It must never be used for a real customer.</List.Item>
            </List>
            <Checkbox
              label="I understand"
              {...demoForm.getInputProps('acknowledged', { type: 'checkbox' })}
            />
            <TextInput
              label="Note (optional)"
              placeholder="For example: OpenAI plugin reviewer"
              {...demoForm.getInputProps('reason')}
            />
          </Stack>
        )}
      </AppModal>
    </>
  );
};
